import React, { useState, useEffect } from "react";
import SuccessModal from "../../Campaigns/CampaignFeature/SuccessModal/SuccessModal";
import LoginSignupModal from "../../shared/LoginSignupModal/LoginSignupModal";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  CurrencyCircleDollar,
  CheckSquare,
  Square,
} from "@phosphor-icons/react";
import {
  generateNPDonation,
  generateNPSubscription,
} from "../../../api/GenerateDonation";
import {
  NPDonationSuccess,
  NPSubscriptionSuccess,
} from "../../../api/DonationSuccess";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";
import PrimaryButton from "../../shared/Buttons/PrimaryButton";

const DonationBoxv2 = ({ nonprofit }) => {
  const [coverFee, setCoverFee] = useState(true);
  const [prevAmount, setPrevAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [, setDonationAmount] = useState(0);
  const [clientSecret, setClientSecret] = useState(null);
  const [donationId, setDonationId] = useState(0);
  const [stripe, setStripe] = useState(null);
  const [, setIsStripeLoading] = useState(true);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [elements, setElements] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoginSignupModalOpen, setLoginSignupModalOpen] = useState(false);
  const [loginOrSignup, setLoginOrSignup] = useState("");
  const [localUserData, setLocalUserData] = useState(null);

  const [selectedTab, setSelectedTab] = useState(0);
  const [donateClicked, setDonateClicked] = useState(false);
  const [ifActiveNPSub, setIfActiveNPSub] = useState(false);

  const userData = useSelector((state) => state.userData.userData);

  const navigate = useNavigate();

  useEffect(() => {
    if (Object.keys(userData).length !== 0) {
      setLocalUserData(JSON.parse(userData));
    }
  }, [userData]);

  useEffect(() => {
    let np_sub = [];
    if (localUserData && nonprofit) {
      if (
        localUserData.np_subscriptions &&
        localUserData.np_subscriptions.length > 0
      ) {
        np_sub = localUserData.np_subscriptions.filter(
          (sub) => nonprofit.org_name === sub.nonprofit && sub.active === true
        );
        np_sub.length > 0 && setIfActiveNPSub(true);
      }
    }
  }, [localUserData, nonprofit]);

  const handleOptionClick = (amount) => {
    // Clear custom amount when selecting preset amount
    setCustomAmount("");

    if (selectedOption === amount) {
      setSelectedOption(null);
      setPrevAmount(0);
      setDonationAmount(0);
    } else {
      setSelectedOption(amount);
      setPrevAmount(amount);
      setDonationAmount(amount);
      setCoverFee(false);
    }
  };

  const handleCoverFeeChange = (e) => {
    setCoverFee(e.target.checked);
    const fee = parseFloat((prevAmount * 0.05).toFixed(2));
    if (e.target.checked) {
      setDonationAmount(Number(prevAmount) + fee);
    } else {
      setDonationAmount(Number(prevAmount));
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    setPrevAmount(value);
    setDonationAmount(parseFloat(value) || 0);

    // Clear selected option when typing in custom amount
    if (selectedOption) {
      setSelectedOption(null);
      setCoverFee(false);
    }
  };

  const handleDonationSubmit = async (event) => {
    event.preventDefault();

    let amountToDonate = selectedOption || parseFloat(customAmount);

    if (coverFee) {
      amountToDonate += parseFloat((amountToDonate * 0.03).toFixed(2));
    }

    setDonationAmount(amountToDonate);

    const initializePayment = async (nonprofit) => {
      if (!nonprofit) return;

      const data = {
        user: localUserData?.id || null,
        nonprofit: nonprofit.id,
        amount: amountToDonate,
      };
      try {
        let response;
        if (selectedTab === 0) {
          // ONE-TIME DONATION
          response = await generateNPDonation(data);
        } else if (selectedTab === 1) {
          // MONTHLY DONATION
          // For testing: Check if price_ids exist, if not, use one-time donation instead
          if (!nonprofit.price_ids || nonprofit.price_ids.length === 0) {
            response = await generateNPDonation(data);
          } else {
            response = await generateNPSubscription(data);
          }
        }
        setClientSecret(response.client_secret);
        setDonationId(response.donation_id);
        setDonateClicked(true);
      } catch (error) {
        console.error("Error: ", error);
      }
    };

    initializePayment(nonprofit);
  };

  useEffect(() => {
    if (clientSecret) {
      const stripeInstance = window.Stripe(process.env.REACT_APP_STRIPE_KEY);
      const appearance = {
        /* appearance */
      };
      const options = {
        layout: {
          type: "tabs",
          defaultCollapsed: false,
        },
      };
      const elementsInstance = stripeInstance.elements({
        clientSecret,
        appearance,
      });
      const cardElement = elementsInstance.create("payment", options);
      cardElement.mount("#payment-element");

      cardElement.on("ready", () => {
        setIsStripeLoading(false);
      });

      setStripe(stripeInstance);
      setElements(elementsInstance);
    }
  }, [clientSecret]);

  const handleSubmitPayment = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    const paymentElement = elements.getElement("payment");

    if (!paymentElement) {
      setPaymentResponse("Payment Element is not loaded.");
      return;
    }

    setIsStripeLoading(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        setPaymentResponse(error.message);
      } else {
        if (paymentIntent && paymentIntent.status === "succeeded") {
          setPaymentResponse({ status: "success" });
          setShowModal(true);
          if (selectedTab === 0) {
            // ONE-TIME DONATION
            await NPDonationSuccess({ donation_id: donationId });
          } else if (selectedTab === 1) {
            // MONTHLY DONATION
            await NPSubscriptionSuccess({ donation_id: donationId });
          }
        } else {
          setPaymentResponse({ error: error });
        }
      }
    } catch (error) {
      setPaymentResponse("An error occurred during payment.");
      console.error(error);
    } finally {
      setIsStripeLoading(false);
    }
  };

  const handleTabChange = (tabIndex) => {
    // Check if user already has active subscription for monthly donations
    if (tabIndex === 1 && ifActiveNPSub) {
      alert(
        "You're already subscribed. Go to your profile to modify your subscription."
      );
      return;
    }

    setSelectedTab(tabIndex);
    setCoverFee(false);
    setCustomAmount("");
    setSelectedOption(null);
    setPrevAmount(0);
    setDonationAmount(0);
  };

  const closeModal = () => {
    setShowModal(false);
    window.location.reload();
  };

  const openLoginSignupModal = (type) => {
    setLoginOrSignup(type);
    setLoginSignupModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLoginSignupModal = () => {
    setLoginSignupModalOpen(false);
    document.body.style.overflow = "";
  };

  const handleFormSuccess = (success) => {
    if (success) {
      window.location.reload();
    }
  };

  return (
    <div
      className={`bg-white rounded-[10px] shadow-[0px_4px_20px_-4px_rgba(0,0,0,0.25)] p-6 sm:p-8 md:p-10 `}
    >
      <h2 className="text-[#46A4DD] text-left !font-poppins !text-2xl sm:!text-3xl font-semibold mb-6 sm:mb-8">
        Support Our Cause
      </h2>

      {!donateClicked && (
        <div className="flex items-center justify-start mb-6 sm:mb-8">
          <div className="relative border border-blue-navy rounded-full p-0 overflow-hidden flex w-full max-w-[250px]">
            <button
              className={`px-4 sm:px-6 py-1 sm:py-1.5 font-montserrat rounded-full !text-xs font-semibold transition-all flex-1 ${
                selectedTab === 0 ? "bg-blue-navy text-white" : "text-black"
              }`}
              onClick={() => handleTabChange(0)}
            >
              One Time
            </button>
            {/* Temporarily showing Monthly button for all nonprofits for testing */}
            <button
              className={`px-4 sm:px-6 py-1 sm:py-1.5 font-montserrat rounded-full !text-xs font-semibold transition-all flex-1 ${
                selectedTab === 1 ? "bg-blue-navy text-white" : "text-black"
              }`}
              onClick={() => handleTabChange(1)}
            >
              Monthly
            </button>
          </div>
        </div>
      )}

      {paymentResponse && paymentResponse.status && showModal ? (
        <SuccessModal
          message="Payment succeeded!"
          onClose={closeModal}
          showModal={showModal}
        />
      ) : null}

      <div>
        {clientSecret ? (
          <form
            id="payment-form"
            className="w-full"
            onSubmit={handleSubmitPayment}
          >
            <div id="payment-element" className="mb-4"></div>
            <SecondaryButton type="submit" className="w-full">
              Complete Donation
            </SecondaryButton>
          </form>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3 md:gap-5 mb-6 sm:mb-8">
              {[10, 25, 50, 100].map((amount) => (
                <button
                  key={amount}
                  className={`${
                    selectedOption === amount
                      ? "bg-[#46A4DD] text-white"
                      : "bg-white text-[#7C7C7C] hover:bg-gray-50"
                  } border border-[#7C7C7C] rounded-lg py-1 sm:py-2 px-2 sm:px-4 md:px-6 !font-montserrat !text-base sm:!text-lg !font-semibold transition-colors`}
                  type="button"
                  onClick={() => handleOptionClick(amount)}
                >
                  ${amount}
                </button>
              ))}
            </div>

            <div className="mb-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-2 sm:left-3 md:left-4 flex items-center pointer-events-none">
                  <CurrencyCircleDollar
                    size={16}
                    className="text-[#7C7C7C] sm:size-[20px] md:size-[24px]"
                  />
                </div>
                <input
                  type="number"
                  id="donation-amount"
                  name="donation-amount"
                  placeholder="Custom Donation Amount"
                  className="w-full h-8 sm:h-9 md:h-10 py-0.5 sm:py-1 px-7 sm:px-9 md:px-12 text-xs sm:text-sm md:text-base border border-[#7C7C7C] rounded-xl font-montserrat text-[#7C7C7C] placeholder:text-[#7C7C7C] placeholder:text-xs sm:placeholder:text-sm md:placeholder:text-base !font-medium focus:outline-none"
                  value={customAmount}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <form className="w-full" onSubmit={handleDonationSubmit}>
              {selectedTab === 0 && (
                <div className="flex items-center gap-1 mb-4 sm:mb-6">
                  <label
                    htmlFor="coverFee"
                    className="text-[#7C7C7C] font-montserrat sm:text-nowrap  !text-xs sm:!text-sm font-normal"
                  >
                    Would you like to cover the transaction fee of ($
                    {(prevAmount * 0.03).toFixed(2)})?
                  </label>
                  <button
                    type="button"
                    onClick={() => setCoverFee(!coverFee)}
                    className="flex items-center justify-center focus:outline-none"
                  >
                    {coverFee ? (
                      <CheckSquare
                        size={16}
                        className="text-[#7C7C7C] sm:text-lg"
                      />
                    ) : (
                      <Square size={16} className="text-[#7C7C7C] sm:text-lg" />
                    )}
                  </button>
                </div>
              )}

              {localUserData && (
                <PrimaryButton
                  type="submit"
                  className="w-full py-2 sm:py-3 bg-[#46A4DD] hover:bg-[#3a91c7] text-white font-montserrat text-[14px] sm:text-[16px] font-semibold rounded-lg transition-colors"
                >
                  Donate
                </PrimaryButton>
              )}
            </form>

            {!localUserData && (
              <div className="mt-4 sm:mt-6 text-center">
                {isLoginSignupModalOpen ? (
                  loginOrSignup === "login" ? (
                    <LoginSignupModal
                      loginOrSignup={"login"}
                      isOpen={openLoginSignupModal}
                      onClose={closeLoginSignupModal}
                      onFormSuccess={handleFormSuccess}
                    />
                  ) : (
                    <LoginSignupModal
                      loginOrSignup={"signup"}
                      isOpen={openLoginSignupModal}
                      onClose={closeLoginSignupModal}
                      onFormSuccess={handleFormSuccess}
                    />
                  )
                ) : null}
                <p className="font-montserrat text-[14px] sm:text-[16px]">
                  <button
                    className="text-[#FE994F] font-bold underline hover:text-[#e8884a]"
                    onClick={() => openLoginSignupModal("signup")}
                  >
                    Sign Up
                  </button>
                  <span className="text-black font-medium mx-2">or</span>
                  <button
                    className="text-[#46A4DD] font-bold underline hover:text-[#3a91c7]"
                    onClick={() => openLoginSignupModal("login")}
                  >
                    Login
                  </button>
                  <span className="text-black font-medium"> to donate</span>
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DonationBoxv2;
